import { createFileRoute } from '@tanstack/react-router';
import { SchedulePage } from '@/components/ApplyPage/SchedulePage';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import {
  getScheduledSessionPublicPublicSessionsScheduledUrlTokenGetOptions,
} from '@/api';
import Loader from '@/components/Loader';
import { ApplyDevToolbar } from '@/components/ApplyPage/ApplyDevToolbar';
import { getThemeConfig } from '@/constants/applyPageThemes';
import { createApplyPageHead } from '@/lib/seo';
import { loadPublicTemplate } from '@/lib/loadPublicTemplate';
import { getDevSample } from '@/components/ApplyPage/devSamples';
import { SessionNotAvailable } from '@/components/SessionNotAvailable';

// Dev-only: inviteToken=preview renders without an application; theme and
// sample work like on the apply page.
const PREVIEW_TOKEN = 'preview';
// Statuses after the interview started: the link shows "done", never the picker.
const DONE_STATUSES: string[] = ['session-started', 'generating-summary', 'completed', 'failed'];
const scheduleSearchSchema = z.object({
  inviteToken: z.string(),
  theme: z.string().optional(),
  sample: z.string().optional(),
});

export const Route = createFileRoute('/apply/$publicId/schedule')({
  validateSearch: scheduleSearchSchema,
  loaderDeps: ({ search }) => ({ sample: search.sample }),
  loader: async ({ params, deps }) => {
    const devSample = getDevSample(deps.sample);
    if (devSample) return { vacancy: devSample };
    return loadPublicTemplate(params.publicId);
  },
  head: ({ loaderData, params }) => {
    const vacancy = loaderData?.vacancy;
    const vacancyName = vacancy?.name || 'Job Application';
    const companyName = vacancy?.company_info?.name || 'Company';
    const companyLogo = vacancy?.company_info?.profile_image || undefined;

    return createApplyPageHead({
      vacancyName,
      companyName,
      companyLogo,
      publicId: params.publicId,
    });
  },
  component: ScheduleRouteComponent,
});

function ScheduleRouteComponent() {
  const { inviteToken, theme: themeOverride, sample } = Route.useSearch();
  const { publicId } = Route.useParams();
  const navigate = Route.useNavigate();
  // An explicit route, not '..': the relative hop lost the public id and showed "Session not available".
  const goBack = () =>
    navigate({
      to: '/apply/$publicId',
      params: { publicId },
      search: import.meta.env.DEV ? { theme: themeOverride, sample } : {},
    });
  const preview = import.meta.env.DEV && inviteToken === PREVIEW_TOKEN;
  const { vacancy, unavailableReason } = Route.useLoaderData();

  const { data: application, isError } = useQuery({
    ...getScheduledSessionPublicPublicSessionsScheduledUrlTokenGetOptions({
      path: { url_token: inviteToken },
    }),
    // Fetched even when the session is unavailable: a candidate who already booked should still see their time.
    enabled: !preview,
    // A missing invite won't appear on retry; a network blip might.
    retry: (count, error: any) => error?.response?.status !== 404 && count < 2,
  });

  const theme = getThemeConfig(
    (import.meta.env.DEV && themeOverride) ||
      vacancy?.company_info?.apply_page_theme,
  );

  if (preview && vacancy) {
    return (
      <>
        <ApplyDevToolbar
          publicId={vacancy.public_id}
          theme={theme}
          companyTheme={vacancy.company_info?.apply_page_theme}
        />
        <SchedulePage roomId="" applicationId="" theme={theme} onBack={goBack} preview />
      </>
    );
  }

  if (isError) {
    return <SessionNotAvailable reason="not-found" />;
  }

  if (!application) {
    // preview without a sample has nothing to load
    return preview ? <SessionNotAvailable reason={unavailableReason} /> : <Loader />;
  }

  const booked = application.status === 'scheduled';
  const cancelled = application.status === 'cancelled';
  if (!vacancy && !booked && !cancelled) {
    return <SessionNotAvailable reason={unavailableReason} />;
  }

  return (
    <SchedulePage
      roomId={application.room_id ?? ''}
      applicationId={application.id ?? ''}
      inviteToken={inviteToken}
      cancelledBy={cancelled ? (application.cancelled_by ?? 'company') : null}
      theme={theme}
      scheduledAt={booked || cancelled ? application.scheduled_at : null}
      interviewDone={DONE_STATUSES.includes(application.status)}
      canReschedule={Boolean(vacancy)}
      onBack={goBack}
    />
  );
}
